import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useApiClient } from '../../config/axios';

export type VariantChoice =
  | {
      source: 'customer';
      source_customer_id: number;
      source_catalog_id: number;
      label?: string;
      formula?: string;
      renderer?: string;
    }
  | {
      source: 'new';
      label?: string;
      formula?: string;
      renderer?: string;
      data_type?: string;
      item_type?: string;
    };

interface VariantPickerModalProps {
  open: boolean;
  dataIndex: string;
  prdCalculation?: string | null;
  onChoose: (choice: VariantChoice) => void;
  onCancel: () => void;
}

interface LibraryRow {
  prd_id: number | null;
  s5_metric_id: number | null;
  prd_display_name: string | null;
  prd_calculation: string | null;
  variant_customer_id: number | null;
  variant_customer_name: string | null;
  variant_label: string | null;
  variant_formula: string | null;
  variant_renderer: string | null;
  variant_data_type: string | null;
  variant_source_type: string | null;
  variant_catalog_id: number | null;
}

const DEFAULT_RENDERERS = ['text', 'number', 'usMoney', 'percent', 'percentTwoDecimal'];
const DATA_TYPES = ['string', 'integer', 'float', 'boolean', 'date'];
const ITEM_TYPES = ['computed_metric', 'base_metrics'];

const formulaPreSx = {
  fontSize: 13,
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap' as const,
  maxHeight: 200,
  overflow: 'auto',
  background: '#F5F5F5',
  padding: '8px',
  borderRadius: '4px',
  margin: '8px 0',
};

const VariantPickerModal: React.FC<VariantPickerModalProps> = ({
  open,
  dataIndex,
  prdCalculation,
  onChoose,
  onCancel,
}) => {
  const apiClient = useApiClient();
  const [variants, setVariants] = useState<LibraryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Define-new form state
  const [newLabel, setNewLabel] = useState('');
  const [newFormula, setNewFormula] = useState('');
  const [newRenderer, setNewRenderer] = useState('');
  const [newDataType, setNewDataType] = useState('');
  const [newItemType, setNewItemType] = useState('computed_metric');
  const [defineExpanded, setDefineExpanded] = useState(false);

  // Reset state when opening for a new dataIndex
  useEffect(() => {
    if (!open) return;
    setVariants([]);
    setLoadError(null);
    setNewLabel('');
    setNewFormula('');
    setNewRenderer('');
    setNewDataType('');
    setNewItemType('computed_metric');
    setDefineExpanded(false);

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Try with import_id=0 placeholder; backend may tolerate or 400.
        const { data } = await apiClient.get('/onboarding/library', {
          params: { import_id: 0, kind: 'metric', search: dataIndex },
        });
        if (cancelled) return;
        const rows: LibraryRow[] = Array.isArray(data) ? data : [];
        // Keep only rows with a real catalog match (variant_customer_id and catalog_id present).
        // Then dedupe by variant_catalog_id.
        const seen = new Set<number>();
        const filtered = rows.filter((r) => {
          if (r.variant_customer_id == null || r.variant_catalog_id == null) return false;
          if (seen.has(r.variant_catalog_id)) return false;
          seen.add(r.variant_catalog_id);
          return true;
        });
        setVariants(filtered);
      } catch (err: any) {
        if (cancelled) return;
        // Soft-fail: show empty variants list and let user define new.
        setVariants([]);
        setLoadError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not load variant library'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, dataIndex, apiClient]);

  // Build renderer options from variants + defaults
  const rendererOptions = useMemo(() => {
    const set = new Set<string>(DEFAULT_RENDERERS);
    variants.forEach((v) => {
      if (v.variant_renderer) set.add(v.variant_renderer);
    });
    return Array.from(set);
  }, [variants]);

  const isStreamlinedConfirm =
    !prdCalculation && !loading && variants.length === 1;

  const isEmptyState =
    !prdCalculation && !loading && variants.length === 0;

  // Auto-expand define-new in empty state
  useEffect(() => {
    if (isEmptyState) setDefineExpanded(true);
  }, [isEmptyState]);

  const handleUsePrd = () => {
    onChoose({
      source: 'new',
      formula: prdCalculation || undefined,
      item_type: 'computed_metric',
    });
  };

  const handleUseVariant = (v: LibraryRow) => {
    onChoose({
      source: 'customer',
      source_customer_id: v.variant_customer_id as number,
      source_catalog_id: v.variant_catalog_id as number,
      label: v.variant_label || undefined,
      formula: v.variant_formula || undefined,
      renderer: v.variant_renderer || undefined,
    });
  };

  const handleSubmitNew = () => {
    onChoose({
      source: 'new',
      label: newLabel || undefined,
      formula: newFormula || undefined,
      renderer: newRenderer || undefined,
      data_type: newDataType || undefined,
      item_type: newItemType || undefined,
    });
  };

  const handleCustomizeFromVariant = (v: LibraryRow) => {
    setNewLabel(v.variant_label || '');
    setNewFormula(v.variant_formula || '');
    setNewRenderer(v.variant_renderer || '');
    setNewDataType(v.variant_data_type || '');
    setDefineExpanded(true);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Pick a definition for &quot;{dataIndex}&quot;</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 13, color: '#666' }}>
              Loading variants…
            </Typography>
          </Box>
        )}

        {loadError && !loading && (
          <Typography sx={{ fontSize: 12, color: 'warning.main', mb: 2 }}>
            {loadError} — you can still define a new variant below.
          </Typography>
        )}

        {/* From PRD panel */}
        {prdCalculation && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>From PRD</Typography>
              <Chip size="small" label="Recommended" color="primary" />
            </Box>
            <pre style={formulaPreSx}>{prdCalculation}</pre>
            <Button variant="contained" onClick={handleUsePrd}>
              Use PRD definition
            </Button>
          </Paper>
        )}

        {/* Streamlined confirmation (no PRD, exactly one variant) */}
        {isStreamlinedConfirm && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography sx={{ mb: 1 }}>
              Use the definition from{' '}
              <strong>{variants[0].variant_customer_name || 'another customer'}</strong>?
            </Typography>
            <pre style={formulaPreSx}>{variants[0].variant_formula || ''}</pre>
            <Typography sx={{ fontSize: 12, color: '#666', mb: 2 }}>
              renderer: {variants[0].variant_renderer || '—'} · type:{' '}
              {variants[0].variant_data_type || '—'} · source:{' '}
              {variants[0].variant_source_type || '—'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => handleUseVariant(variants[0])}>
                Confirm
              </Button>
              <Button variant="outlined" onClick={() => handleCustomizeFromVariant(variants[0])}>
                Customize
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Variants list (when not streamlined) */}
        {!isStreamlinedConfirm && variants.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: '#555' }}>
              Variants from other customers
            </Typography>
            {variants.map((v) => (
              <Paper key={v.variant_catalog_id ?? `${v.variant_customer_id}-${v.variant_label}`} sx={{ p: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={v.variant_customer_name || `Customer ${v.variant_customer_id}`} />
                  {v.variant_label && (
                    <Typography sx={{ fontSize: 13, color: '#444' }}>
                      · {v.variant_label}
                    </Typography>
                  )}
                </Box>
                <pre style={formulaPreSx}>{v.variant_formula || ''}</pre>
                <Typography sx={{ fontSize: 12, color: '#666', mb: 1 }}>
                  renderer: {v.variant_renderer || '—'} · type:{' '}
                  {v.variant_data_type || '—'} · source: {v.variant_source_type || '—'}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => handleUseVariant(v)}>
                  Use this
                </Button>
              </Paper>
            ))}
          </Box>
        )}

        {/* Define new (always available) */}
        <Accordion
          expanded={defineExpanded}
          onChange={(_, expanded) => setDefineExpanded(expanded)}
          sx={{ display: 'block' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Define new</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Formula"
                value={newFormula}
                onChange={(e) => setNewFormula(e.target.value)}
                multiline
                rows={4}
                fullWidth
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Renderer</InputLabel>
                <Select
                  label="Renderer"
                  value={newRenderer}
                  onChange={(e) => setNewRenderer(String(e.target.value))}
                >
                  <MenuItem value="">—</MenuItem>
                  {rendererOptions.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Data Type</InputLabel>
                <Select
                  label="Data Type"
                  value={newDataType}
                  onChange={(e) => setNewDataType(String(e.target.value))}
                >
                  <MenuItem value="">—</MenuItem>
                  {DATA_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Item Type</InputLabel>
                <Select
                  label="Item Type"
                  value={newItemType}
                  onChange={(e) => setNewItemType(String(e.target.value))}
                >
                  {ITEM_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Button variant="contained" onClick={handleSubmitNew}>
                  Use this definition
                </Button>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariantPickerModal;
